import { db } from "./firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  getDoc,
  doc,
  deleteDoc,
  updateDoc,
  query,
  where,
  arrayUnion,
  setDoc,
  limit,
  startAfter,
  orderBy,
  getCountFromServer,
  writeBatch,
  onSnapshot,
} from "firebase/firestore";
import { i18n } from "@/i18n";
// 🚀 CACHE GLOBAL para optimización
const CACHE = {
  guides: new Map(),
  categories: null,
  counts: new Map(),
  lastUpdate: null,
  cacheDuration: 10 * 60 * 1000, // 10 minutos
  listeners: new Map(),
};

// 🚀 UTILIDADES DE CACHE
const cacheUtils = {
  isExpired: (timestamp) => {
    return !timestamp || Date.now() - timestamp > CACHE.cacheDuration;
  },

  clearCache: () => {
    CACHE.guides.clear();
    CACHE.categories = null;
    CACHE.counts.clear();
    CACHE.lastUpdate = null;
    console.log("🗑️ Cache limpiado");
  },

  getCacheKey: (collection, filters = {}) => {
    return `${collection}_${JSON.stringify(filters)}`;
  },
};

const practicalGuideData = {
  // 🚀 OPTIMIZADO: Obtener conteo total de guías (muy rápido)
  getGuidesCount: async (category = null) => {
    const cacheKey = cacheUtils.getCacheKey("count", { category });

    // Verificar cache
    if (
      CACHE.counts.has(cacheKey) &&
      !cacheUtils.isExpired(CACHE.counts.get(cacheKey).timestamp)
    ) {
      return CACHE.counts.get(cacheKey).count;
    }

    try {
      let q = collection(db, "practicalGuide");
      if (category) {
        q = query(q, where("category", "==", category));
      }

      const snapshot = await getCountFromServer(q);
      const count = snapshot.data().count;

      // Guardar en cache
      CACHE.counts.set(cacheKey, { count, timestamp: Date.now() });

      return count;
    } catch (error) {
      console.error("Error al obtener conteo:", error);
      return 0;
    }
  },

  // 🚀 OPTIMIZADO: Obtener guías con paginación y cache
  getAllGuides: async (options = {}) => {
    const {
      page = 1,
      pageSize = 25,
      category = null,
      difficulty = null,
      useCache = true,
    } = options;

    // 🚀 COMPATIBILIDAD: Si no hay parámetros, retornar array simple (compatibilidad hacia atrás)
    if (Object.keys(options).length === 0) {
      console.log(
        "🔄 getAllGuides llamado sin parámetros - retornando array simple para compatibilidad"
      );
      const practicalGuides = await practicalGuideData.getAllGuidesAsArray(
        useCache
      );
      console.log(`🚀 Retornando ${practicalGuides.length} guías`);
      console.log(practicalGuides);
      return practicalGuides;
    }

    const cacheKey = cacheUtils.getCacheKey("guides", {
      page,
      pageSize,
      category,
      difficulty,
    });

    // Verificar cache
    if (
      useCache &&
      CACHE.guides.has(cacheKey) &&
      !cacheUtils.isExpired(CACHE.guides.get(cacheKey).timestamp)
    ) {
      console.log("📦 Usando cache para guías");
      return CACHE.guides.get(cacheKey).data;
    }

    try {
      let q = collection(db, "practicalGuide");

      // Aplicar filtros
      if (category) {
        q = query(q, where("category", "==", category));
      }
      if (difficulty) {
        q = query(q, where("difficulty", "==", difficulty));
      }

      // Ordenar por fecha de actualización (más reciente primero)
      q = query(q, orderBy("updatedAt", "desc"));

      // Aplicar paginación
      const startIndex = (page - 1) * pageSize;
      q = query(q, limit(pageSize));

      const querySnapshot = await getDocs(q);

      console.log(`🚀 QuerySnapshot: ${querySnapshot.docs.length} guías`);
      const guides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const result = {
        guides,
        page,
        pageSize,
        total: guides.length,
        hasMore: guides.length === pageSize,
      };

      // Guardar en cache
      CACHE.guides.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      return result;
    } catch (error) {
      console.error("Error al obtener guías:", error);
      throw error;
    }
  },

  // 🚀 NUEVO: Obtener guías publicadas (array simple para compatibilidad)
  getPublishedGuides: async (useCache = true) => {
    const cacheKey = cacheUtils.getCacheKey("published_guides");

    // Verificar cache
    if (
      useCache &&
      CACHE.guides.has(cacheKey) &&
      !cacheUtils.isExpired(CACHE.guides.get(cacheKey).timestamp)
    ) {
      console.log("📦 Usando cache para guías publicadas");
      return CACHE.guides.get(cacheKey).data;
    }

    try {
      let q = collection(db, "practicalGuide");

      // Solo guías publicadas
      q = query(q, where("isPublished", "==", true));

      // Ordenar por fecha de actualización (más reciente primero)
      q = query(q, orderBy("updatedAt", "desc"));

      const querySnapshot = await getDocs(q);
      const guides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Guardar en cache
      CACHE.guides.set(cacheKey, {
        data: guides,
        timestamp: Date.now(),
      });

      return guides; // Retorna array simple para compatibilidad
    } catch (error) {
      console.error("Error al obtener guías publicadas:", error);
      return [];
    }
  },

  // 🚀 NUEVO: Obtener todas las guías como array simple (para compatibilidad)
  getAllGuidesAsArray: async (useCache = true) => {
    const cacheKey = cacheUtils.getCacheKey("all_guides_array");

    // Verificar cache
    if (
      useCache &&
      CACHE.guides.has(cacheKey) &&
      !cacheUtils.isExpired(CACHE.guides.get(cacheKey).timestamp)
    ) {
      console.log("📦 Usando cache para todas las guías (array)");
      return CACHE.guides.get(cacheKey).data;
    }

    try {
      let q = collection(db, "practicalGuide");

      // Ordenar por fecha de actualización (más reciente primero)
      q = query(q, orderBy("updatedAt", "desc"));

      const querySnapshot = await getDocs(q);
      const guides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Guardar en cache
      CACHE.guides.set(cacheKey, {
        data: guides,
        timestamp: Date.now(),
      });

      return guides; // Retorna array simple para compatibilidad
    } catch (error) {
      console.error("Error al obtener todas las guías:", error);
      return [];
    }
  },

  // 🚀 NUEVO: Obtener guías por categoría con paginación
  getGuidesByCategory: async (category, options = {}) => {
    return await practicalGuideData.getAllGuides({ ...options, category });
  },

  // 🚀 NUEVO: Obtener guías por dificultad con paginación
  getGuidesByDifficulty: async (difficulty, options = {}) => {
    return await practicalGuideData.getAllGuides({ ...options, difficulty });
  },

  // 🚀 OPTIMIZADO: Obtener guías con scroll infinito
  getGuidesInfiniteScroll: async (
    category = null,
    lastDoc = null,
    pageSize = 20
  ) => {
    try {
      let q = collection(db, "practicalGuide");

      if (category) {
        q = query(q, where("category", "==", category));
      }

      q = query(q, orderBy("updatedAt", "desc"), limit(pageSize));

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const guides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return {
        guides,
        lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
        hasMore: querySnapshot.docs.length === pageSize,
      };
    } catch (error) {
      console.error("Error en scroll infinito:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Obtener guías con filtros avanzados
  getGuidesWithFilters: async (filters = {}, options = {}) => {
    const { page = 1, pageSize = 25, useCache = true } = options;

    const cacheKey = cacheUtils.getCacheKey("guides_filtered", {
      filters,
      page,
      pageSize,
    });

    // Verificar cache
    if (
      useCache &&
      CACHE.guides.has(cacheKey) &&
      !cacheUtils.isExpired(CACHE.guides.get(cacheKey).timestamp)
    ) {
      return CACHE.guides.get(cacheKey).data;
    }

    try {
      let q = collection(db, "practicalGuide");
      const constraints = [];

      // Aplicar filtros dinámicamente
      if (filters.category) {
        constraints.push(where("category", "==", filters.category));
      }
      if (filters.difficulty) {
        constraints.push(where("difficulty", "==", filters.difficulty));
      }
      if (filters.isPublished !== undefined) {
        constraints.push(where("isPublished", "==", filters.isPublished));
      }
      if (filters.isActive !== undefined) {
        constraints.push(where("isActive", "==", filters.isActive));
      }

      // Ordenar
      constraints.push(orderBy("updatedAt", "desc"));

      // Paginación
      constraints.push(limit(pageSize));

      q = query(q, ...constraints);
      const querySnapshot = await getDocs(q);

      const guides = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const result = {
        guides,
        page,
        pageSize,
        total: guides.length,
        hasMore: guides.length === pageSize,
        filters,
      };

      // Guardar en cache
      CACHE.guides.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      return result;
    } catch (error) {
      console.error("Error al obtener guías con filtros:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Obtener un documento específico con cache
  getGuideById: async (guideId, useCache = true) => {
    const cacheKey = cacheUtils.getCacheKey("guide", { id: guideId });

    // Verificar cache
    if (
      useCache &&
      CACHE.guides.has(cacheKey) &&
      !cacheUtils.isExpired(CACHE.guides.get(cacheKey).timestamp)
    ) {
      return CACHE.guides.get(cacheKey).data;
    }

    try {
      const docRef = doc(db, "practicalGuide", guideId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const guideData = { id: docSnap.id, ...docSnap.data() };

        // Guardar en cache
        CACHE.guides.set(cacheKey, {
          data: guideData,
          timestamp: Date.now(),
        });

        return guideData;
      } else {
        throw new Error("Guide not found");
      }
    } catch (error) {
      console.error("Error al obtener guía por ID:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Crear guía y limpiar cache relacionado
  createGuide: async (guideData) => {
    try {
      if (!guideData || !guideData.name) {
        throw new Error("Datos de la guía inválidos");
      }

      const guideId = `practical_${Date.now()}${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const newGuideData = {
        id: guideId,
        name: guideData.name,
        description: guideData.description || "",
        category: guideData.category || "robotics",
        difficulty: guideData.difficulty || "beginner",
        estimatedTime: guideData.estimatedTime || 60,
        image: guideData.image || "",
        isActive: guideData.isActive !== undefined ? guideData.isActive : true,
        isPublished:
          guideData.isPublished !== undefined ? guideData.isPublished : false,
        InitialProjectDescription:
          guideData.InitialProjectDescription ||
          "Descripción inicial del proyecto",
        project: {
          name: guideData.name,
          code: guideData.code || "// Código del proyecto",
          components: guideData.components || [],
          connections: guideData.connections || [],
        },
        steps: guideData.steps || [
          {
            stepName: "Paso 1: Configuración inicial",
            description: "Configura los componentes básicos para la actividad.",
            details: [
              "Revisa los materiales necesarios",
              "Prepara tu espacio de trabajo",
            ],
            explanation: "Este es el primer paso para comenzar",
            image:
              "https://firebasestorage.googleapis.com/v0/b/mvp-inventech-testing.appspot.com/o/images%2FimageDefault.png?alt=media&token=456a8bd7-1b1b-4973-bde2-c378e4b6a777",
          },
        ],
        tags: guideData.tags || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const guideRef = doc(db, "practicalGuide", guideId);
      await setDoc(guideRef, newGuideData);

      // 🚀 Limpiar cache relacionado
      cacheUtils.clearCache();

      return { id: guideId, ...newGuideData };
    } catch (error) {
      console.error("Error al crear guía práctica:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Actualizar guía y limpiar cache
  updateGuide: async (guideId, updateData) => {
    try {
      const guideRef = doc(db, "practicalGuide", guideId);

      const updatedData = {
        ...updateData,
        updatedAt: new Date().toISOString(),
      };

      if (updateData.project && typeof updateData.project === "string") {
        updatedData.project = { code: updateData.project };
      }

      if (updateData.steps && Array.isArray(updateData.steps)) {
        updatedData.steps = updateData.steps.map((step) => ({
          stepName: step.stepName || "Paso sin nombre",
          description: step.description || "",
          details: step.details || [],
          explanation: step.explanation || "",
          image:
            step.image ||
            "https://firebasestorage.googleapis.com/v0/b/mvp-inventech-testing.appspot.com/o/images%2FimageDefault.png?alt=media&token=456a8bd7-1b1b-4973-bde2-c378e4b6a777",
          ...step,
        }));
      }

      await updateDoc(guideRef, updatedData);

      // 🚀 Limpiar cache relacionado
      cacheUtils.clearCache();

      const updatedDoc = await getDoc(guideRef);
      return { id: updatedDoc.id, ...updatedDoc.data() };
    } catch (error) {
      console.error("Error al actualizar guía práctica:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Eliminar guía y limpiar cache
  deleteGuide: async (guideId) => {
    try {
      const guideRef = doc(db, "practicalGuide", guideId);
      await deleteDoc(guideRef);

      // 🚀 Limpiar cache relacionado
      cacheUtils.clearCache();

      return { success: true, message: "Guía eliminada correctamente" };
    } catch (error) {
      console.error("Error al eliminar guía práctica:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Duplicar guía
  duplicateGuide: async (guideId) => {
    try {
      const originalGuide = await practicalGuideData.getGuideById(guideId);

      const duplicatedGuideData = {
        ...originalGuide,
        name: `${originalGuide.name} (Copia)`,
        isPublished: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      delete duplicatedGuideData.id;

      return await practicalGuideData.createGuide(duplicatedGuideData);
    } catch (error) {
      console.error("Error al duplicar guía práctica:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Agregar componente
  addComponentToGuide: async (guideId, component) => {
    const guideRef = doc(db, "practicalGuide", guideId);
    await updateDoc(guideRef, {
      components: arrayUnion(component),
      updatedAt: new Date().toISOString(),
    });

    // Limpiar cache
    cacheUtils.clearCache();
  },

  // 🚀 OPTIMIZADO: Eliminar componente
  removeComponentFromGuide: async (guideId, idLocal) => {
    const guideRef = doc(db, "practicalGuide", guideId);
    const guideSnap = await getDoc(guideRef);

    if (guideSnap.exists()) {
      const guideData = guideSnap.data();
      const updatedComponents = guideData.components.filter(
        (component) => component.id !== idLocal
      );

      await updateDoc(guideRef, {
        components: updatedComponents,
        updatedAt: new Date().toISOString(),
      });

      // Limpiar cache
      cacheUtils.clearCache();
    } else {
      throw new Error("Guide not found");
    }
  },

  // 🚀 OPTIMIZADO: Obtener categorías con cache
  getCategories: async (useCache = true) => {
    if (
      useCache &&
      CACHE.categories &&
      !cacheUtils.isExpired(CACHE.lastUpdate)
    ) {
      return CACHE.categories;
    }

    try {
      const querySnapshot = await getDocs(
        collection(db, "CategoriesPracticalGuides")
      );
      const categories = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Guardar en cache
      CACHE.categories = categories;
      CACHE.lastUpdate = Date.now();

      return categories;
    } catch (error) {
      console.error("Error al obtener categorías:", error);
      return [];
    }
  },

  // 🚀 OPTIMIZADO: Crear categoría
  createCategory: async (categoryData) => {
    try {
      if (!categoryData || !categoryData.value || !categoryData.text) {
        throw new Error("Datos de la categoría inválidos");
      }

      const docRef = await addDoc(
        collection(db, "CategoriesPracticalGuides"),
        categoryData
      );

      // Limpiar cache
      CACHE.categories = null;

      return { id: docRef.id, ...categoryData };
    } catch (error) {
      console.error("Error al crear categoría:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Actualizar categoría
  updateCategory: async (categoryValue, updatedData) => {
    try {
      const q = query(
        collection(db, "CategoriesPracticalGuides"),
        where("value", "==", categoryValue)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Categoría no encontrada");
      }

      const docRef = doc(
        db,
        "CategoriesPracticalGuides",
        querySnapshot.docs[0].id
      );
      await updateDoc(docRef, updatedData);

      // Limpiar cache
      CACHE.categories = null;

      return { success: true, message: "Categoría actualizada correctamente" };
    } catch (error) {
      console.error("Error al actualizar categoría:", error);
      throw error;
    }
  },

  // 🚀 OPTIMIZADO: Eliminar categoría
  deleteCategory: async (categoryValue) => {
    try {
      const q = query(
        collection(db, "CategoriesPracticalGuides"),
        where("value", "==", categoryValue)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("Categoría no encontrada");
      }

      const docRef = doc(
        db,
        "CategoriesPracticalGuides",
        querySnapshot.docs[0].id
      );
      await deleteDoc(docRef);

      // Limpiar cache
      CACHE.categories = null;

      return { success: true, message: "Categoría eliminada correctamente" };
    } catch (error) {
      console.error("Error al eliminar categoría:", error);
      throw error;
    }
  },

  // 🚀 NUEVO: Obtener estadísticas rápidas
  getStats: async () => {
    try {
      const [totalCount, publishedCount, activeCount] = await Promise.all([
        practicalGuideData.getGuidesCount(),
        practicalGuideData.getGuidesCount("published"),
        practicalGuideData.getGuidesCount("active"),
      ]);

      return {
        total: totalCount,
        published: publishedCount,
        active: activeCount,
        draft: totalCount - publishedCount,
      };
    } catch (error) {
      console.error("Error al obtener estadísticas:", error);
      return { total: 0, published: 0, active: 0, draft: 0 };
    }
  },

  // 🚀 NUEVO: Limpiar cache manualmente
  clearCache: () => {
    cacheUtils.clearCache();
  },

  // 🚀 NUEVO: Obtener estado del cache
  getCacheStatus: () => {
    return {
      guidesCacheSize: CACHE.guides.size,
      categoriesCache: !!CACHE.categories,
      countsCacheSize: CACHE.counts.size,
      lastUpdate: CACHE.lastUpdate,
      isExpired: cacheUtils.isExpired(CACHE.lastUpdate),
    };
  },

  // 🚀 NUEVO: Suscribirse a cambios en tiempo real (opcional)
  subscribeToChanges: (callback) => {
    const unsubscribe = onSnapshot(
      collection(db, "practicalGuide"),
      (snapshot) => {
        // Limpiar cache cuando hay cambios
        cacheUtils.clearCache();
        if (callback) callback(snapshot);
      }
    );

    return unsubscribe;
  },

  // 🚀 NUEVO: Obtener guías en lotes para mejor rendimiento
  getGuidesBatch: async (guideIds, useCache = true) => {
    const results = [];
    const uncachedIds = [];

    // Verificar cache primero
    for (const id of guideIds) {
      const cacheKey = cacheUtils.getCacheKey("guide", { id });
      if (
        useCache &&
        CACHE.guides.has(cacheKey) &&
        !cacheUtils.isExpired(CACHE.guides.get(cacheKey).timestamp)
      ) {
        results.push(CACHE.guides.get(cacheKey).data);
      } else {
        uncachedIds.push(id);
      }
    }

    // Obtener guías no cacheadas en lotes
    if (uncachedIds.length > 0) {
      const batchSize = 10; // Firestore permite máximo 10 en un batch
      for (let i = 0; i < uncachedIds.length; i += batchSize) {
        const batch = uncachedIds.slice(i, i + batchSize);
        const batchPromises = batch.map((id) =>
          practicalGuideData.getGuideById(id, false)
        );
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      }
    }

    return results;
  },

  // 🚀 NUEVO: Obtener niveles de dificultad
  getDifficultyLevels: () => {
    return [
      { value: "beginner", text: i18n.t('recommended-projects.beginner') },
      { value: "intermediate", text: i18n.t('recommended-projects.intermediate') },
      { value: "advanced", text: i18n.t('recommended-projects.advanced') },
    ];
  },
};

export default practicalGuideData;
